import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AppNotification } from "@/lib/notifications";
import {
  playNotificationTing,
  unlockNotificationAudio,
} from "@/lib/notificationSound";

interface Options {
  enabled: boolean;
  initialized: boolean;
  items: AppNotification[];
  refresh?: () => void;
}

// Lưới an toàn: nếu realtime lỡ trễ hoặc chớp, tự làm mới định kỳ để popup chắc
// chắn hiện ra khi có mã trùng mới.
//
// Để ở 60 giây (thay vì 15 giây như trước): thông báo mới đã được realtime đẩy
// về tức thì, nên đây chỉ là phương án dự phòng. Hỏi database mỗi 15 giây làm
// trình duyệt liên tục nhận dữ liệu mới và vẽ lại giao diện, gây cảm giác
// "trang tự tải lại" rất khó chịu.
const POLL_INTERVAL_MS = 60000;

// Khi người dùng tạm ẩn popup (ví dụ bấm "Tới Báo cáo để xử lý") mà CHƯA đánh
// dấu đã xử lý, popup sẽ TỰ ĐỘNG hiện lại sau khoảng thời gian này.
const DISMISS_REOPEN_MS = 120000;

// Popup cảnh báo xuất trùng được SUY RA trực tiếp từ dữ liệu thay vì dựa vào
// việc "bắt sự kiện realtime đến muộn". Nhờ vậy:
//   - Có mã trùng mới -> popup tự hiện (dù realtime nhanh hay chậm).
//   - Vẫn còn khi admin tải lại trang nếu mã trùng CHƯA được xử lý.
//   - Chỉ biến mất khi thông báo được đánh dấu đã xử lý (da_doc = true).
// Lần đầu tải trang chỉ "ghi nhớ" cảnh báo sẵn có để KHÔNG dội âm thanh hàng
// loạt, nhưng popup vẫn hiện bình thường.
export function useNotificationAlerts({
  enabled,
  initialized,
  items,
  refresh,
}: Options) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const seenRef = useRef<Set<string>>(new Set());
  const primedRef = useRef(false);
  const reopenTimersRef = useRef<Record<string, number>>({});

  // Xóa bộ đếm mở lại popup (toàn bộ hoặc theo danh sách id).
  const clearReopenTimers = useCallback((ids?: string[]) => {
    const timers = reopenTimersRef.current;
    if (ids && ids.length > 0) {
      ids.forEach((id) => {
        if (timers[id]) {
          window.clearTimeout(timers[id]);
          delete timers[id];
        }
      });
      return;
    }
    Object.values(timers).forEach((t) => window.clearTimeout(t));
    reopenTimersRef.current = {};
  }, []);

  // Hẹn giờ tự bật lại popup cho các cảnh báo vừa bị ẩn tạm.
  const scheduleReopen = useCallback((ids: string[]) => {
    ids.forEach((id) => {
      if (reopenTimersRef.current[id]) {
        window.clearTimeout(reopenTimersRef.current[id]);
      }
      reopenTimersRef.current[id] = window.setTimeout(() => {
        delete reopenTimersRef.current[id];
        setDismissedIds((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, DISMISS_REOPEN_MS);
    });
  }, []);

  // Danh sách cảnh báo đang cần chặn thao tác: thông báo xuất trùng CHƯA ĐỌC
  // (tức chưa xử lý) và chưa bị ẩn tạm trong phiên hiện tại.
  const alerts = useMemo(() => {
    if (!enabled || !initialized) return [];
    return items.filter(
      (n) => n.loai === "trung_lap" && !n.da_doc && !dismissedIds.has(n.id)
    );
  }, [enabled, initialized, items, dismissedIds]);

  // Đăng xuất / mất quyền -> xóa toàn bộ trạng thái đã ghi nhớ.
  useEffect(() => {
    if (!enabled) {
      primedRef.current = false;
      seenRef.current.clear();
      setDismissedIds(new Set());
      clearReopenTimers();
    }
  }, [enabled, clearReopenTimers]);

  // Phát tiếng "ting" khi xuất hiện cảnh báo MỚI chưa từng hiện trong phiên này.
  useEffect(() => {
    if (!enabled || !initialized) return;
    const unreadDupIds = items
      .filter((n) => n.loai === "trung_lap" && !n.da_doc)
      .map((n) => n.id);

    if (!primedRef.current) {
      unreadDupIds.forEach((id) => seenRef.current.add(id));
      primedRef.current = true;
      return;
    }

    const fresh = unreadDupIds.filter((id) => !seenRef.current.has(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => seenRef.current.add(id));
    playNotificationTing();
  }, [enabled, initialized, items]);

  // Mở khóa âm thanh ngay khi admin/quản lý kho đăng nhập.
  useEffect(() => {
    if (enabled) unlockNotificationAudio();
  }, [enabled]);

  // Lưới an toàn: tự làm mới dữ liệu định kỳ phòng khi realtime không tới kịp.
  // `refresh` là bản cập nhật NGẦM: không bật loading, không xoá danh sách cũ.
  // Chỉ chạy khi tab đang được xem mới thật sự cần hỏi lại.
  useEffect(() => {
    if (!enabled || !refresh) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      refresh();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [enabled, refresh]);

  const dismiss = useCallback(
    (id: string) => {
      setDismissedIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      scheduleReopen([id]);
    },
    [scheduleReopen]
  );

  const dismissAll = useCallback(() => {
    const ids = items
      .filter((n) => n.loai === "trung_lap" && !n.da_doc)
      .map((n) => n.id);
    setDismissedIds((prev) => {
      const next = new Set(prev);
      items.forEach((n) => {
        if (n.loai === "trung_lap" && !n.da_doc) next.add(n.id);
      });
      return next;
    });
    scheduleReopen(ids);
  }, [items, scheduleReopen]);

  // Dọn bộ đếm khi component unmount.
  useEffect(() => {
    return () => clearReopenTimers();
  }, [clearReopenTimers]);

  return { alerts, dismiss, dismissAll };
}